import uuid from 'uuid/v4';
import { Resource } from '../types/DataExplorer';
import { DataTreeNode, TreeNodeProperty } from '../types/Tree';
import { deepClone } from './Helpers';
import { getDomain } from './UtilFunctions';

const EXCLUDE_KEYS = ['vmid', 'children', 'properties', 'propertyMap'];

export const convertTreeNodeToResource = (node: DataTreeNode): Resource => {
  const referenceProp = node.properties.find(
    p => p.name === 'reference'
  ) as TreeNodeProperty;
  const sourceProp = node.properties.find(
    p => p.name === 'source'
  ) as TreeNodeProperty;
  return {
    vmid: node.vmid,
    name: node.name,
    reference: referenceProp.value,
    source: sourceProp.value
  };
};

export const convertResourceToTreeNode = (resource: Resource): DataTreeNode => {
  return {
    vmid: resource.vmid,
    name: resource.name,
    state: 'collapsed',
    reference: false,
    properties: Object.keys(resource)
      .filter(k => EXCLUDE_KEYS.indexOf(k) < 0)
      .map(k => ({ name: k, value: resource[k] })),
    children: []
  };
};

export const convertResourcesToTree = (
  resources: Resource[]
): DataTreeNode[] => {
  const referenceNodes = getDomain(resources.map(r => r.reference));
  return referenceNodes.map<DataTreeNode>(node => ({
    vmid: uuid(),
    name: node,
    state: 'collapsed',
    reference: true,
    properties: [{ name: 'name', value: node }],
    children: resources
      .filter(r => r.reference === node)
      .map<DataTreeNode>(r => ({
        vmid: r.vmid,
        name: r.name,
        state: 'collapsed',
        reference: false,
        properties: Object.keys(r)
          .filter(k => EXCLUDE_KEYS.indexOf(k) < 0)
          .map(k => ({ name: k, value: r[k] })),
        children: []
      }))
  }));
};

export const convertTreeToResources = (tree: DataTreeNode[]): Resource[] => {
  const resources: Resource[] = [];
  tree.forEach(reference => {
    reference.children.forEach(resource => {
      const sourceProp = resource.properties.find(
        p => p.name === 'source'
      ) as TreeNodeProperty;
      resources.push({
        vmid: resource.vmid,
        name: resource.name,
        reference: reference.name,
        source: sourceProp.value
      });
    });
  });
  return resources;
};

export const changeTreeState = (
  tree: DataTreeNode[],
  vmid: string,
  children?: DataTreeNode[]
): DataTreeNode[] => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndUpdateState(node, vmid, children);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

const findAndUpdateState = (
  node: DataTreeNode,
  vmid: string,
  children?: DataTreeNode[]
): boolean => {
  if (node.vmid === vmid) {
    node.state = children
      ? 'expanded'
      : node.state === 'expanded'
      ? 'collapsed'
      : node.reference
      ? 'expanded'
      : 'loading';
    if (!node.reference) {
      node.children = children || [];
    }
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndUpdateState(node.children[i], vmid, children);
    }
    return updated;
  }
  return false;
};

export const addResourceToTree = (
  tree: DataTreeNode[],
  resource: Resource
): DataTreeNode[] => {
  if (resource.source === 'ROOT') {
    return convertResourcesToTree([...convertTreeToResources(tree), resource]);
  }
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndAddResource(node, resource);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

export const findAndAddResource = (
  node: DataTreeNode,
  resource: Resource
): boolean => {
  if (node.vmid === resource.source) {
    node.children = convertResourcesToTree([
      ...convertTreeToResources(node.children),
      resource
    ]);
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndAddResource(node.children[i], resource);
    }
    return updated;
  }
  return false;
};

export const editResourceInTree = (
  tree: DataTreeNode[],
  resource: Resource
): DataTreeNode[] => {
  if (resource.source === 'ROOT') {
    const resources = convertTreeToResources(tree);
    const i = resources.findIndex(r => r.vmid === resource.vmid);
    resources[i] = resource;
    return convertResourcesToTree(resources);
  }
  const deepClonedTree: DataTreeNode[] = deepClone(tree);
  for (const reference of deepClonedTree) {
    for (const node of reference.children) {
      const updated = findAndUpdateResource(node, resource);
      if (updated) {
        break;
      }
    }
  }
  return deepClonedTree;
};

export const findAndUpdateResource = (
  node: DataTreeNode,
  resource: Resource
): boolean => {
  const resources = convertTreeToResources(node.children);
  const updatedIndex = resources.findIndex(r => r.vmid === resource.vmid);
  if (updatedIndex > -1) {
    resources[updatedIndex] = resource;
    node.children = convertResourcesToTree(resources);
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      const reference = node.children[i];
      for (let j = 0; !updated && j < reference.children.length; j++) {
        updated = findAndUpdateResource(reference.children[j], resource);
      }
    }
    return updated;
  }
  return false;
};

export const deleteResourceFromTree = (tree: DataTreeNode[], vmid: string) => {
  const deepClonedTree = deepClone(tree);
  for (const node of deepClonedTree) {
    const updated = findAndDeleteResource(node, vmid);
    if (updated) {
      break;
    }
  }
  return deepClonedTree;
};

export const findAndDeleteResource = (
  node: DataTreeNode,
  vmid: string
): boolean => {
  const deleteIndex = node.children.findIndex(n => n.vmid === vmid);
  if (deleteIndex > -1) {
    node.children = node.children.filter(n => n.vmid !== vmid);
    return true;
  } else if (node.children) {
    let updated = false;
    for (let i = 0; !updated && i < node.children.length; i++) {
      updated = findAndDeleteResource(node.children[i], vmid);
    }
    return updated;
  }
  return false;
};
